import React, { useRef, useState } from "react"

// Helper function to safely get CSRF token for SSR compatibility
const getCSRFToken = () => {
  if (typeof document === "undefined") return ""
  return (
    document.querySelector('[name="csrf-token"]')?.content ||
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ||
    ""
  )
}

const VideoManualUploadForm = ({ clients = [], onComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  // metadata
  const [clientId, setClientId] = useState(clients?.[0]?.id || "")
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [rating, setRating] = useState("")
  const [recordedAt, setRecordedAt] = useState("")

  const allowedTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"]
  const maxSize = 100 * 1024 * 1024 // 100MB

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Invalid file type. Please upload MP4, MOV, AVI, or WebM files only.")
    }
    if (file.size > maxSize) {
      throw new Error("File too large. Maximum size is 100MB.")
    }
    return true
  }

  const handleFileSelect = (file) => {
    try {
      validateFile(file)
      setSelectedFile(file)
    } catch (error) {
      if (typeof window !== "undefined") alert(error.message)
    }
  }

  const handleFileInputChange = (event) => {
    const file = event.target.files[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setIsDragOver(false)
    const files = event.dataTransfer.files
    if (files.length > 0) handleFileSelect(files[0])
  }

  const uploadToS3 = async (file, uploadUrl) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 90)
          setUploadProgress(progress)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve()
        } else {
          reject(new Error("Upload failed"))
        }
      })

      xhr.addEventListener("error", () => reject(new Error("Upload failed")))

      xhr.open("PUT", uploadUrl)
      xhr.setRequestHeader("Content-Type", file.type)
      xhr.send(file)
    })

  const handleUpload = async () => {
    if (!selectedFile || uploading) return

    try {
      setUploading(true)
      setUploadProgress(0)

      // 1) Get presigned URL for current pro (no upload hash needed)
      const params = new URLSearchParams({
        filename: selectedFile.name,
        content_type: selectedFile.type
      })
      const presignedResponse = await fetch(`/api/videos/presigned_url?${params.toString()}`, {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      })
      const presignedData = await presignedResponse.json()
      if (!presignedResponse.ok || !presignedData.success) {
        throw new Error(presignedData.error || "Failed to get upload URL")
      }

      // 2) Upload to S3
      await uploadToS3(selectedFile, presignedData.data.upload_url)

      // 3) Start transcoding and create video review record
      setUploadProgress(95)
      const startResponse = await fetch(`/api/videos/start_transcoding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCSRFToken()
        },
        body: JSON.stringify({
          video_key: presignedData.data.video_key,
          filename: selectedFile.name,
          content_type: selectedFile.type,
          file_size: selectedFile.size,
          client_id: clientId || null,
          title: title || null,
          description: description || null,
          rating: rating ? parseInt(rating, 10) : null,
          recorded_at: recordedAt || null
        })
      })
      const startData = await startResponse.json()
      if (!startResponse.ok || !startData.success) {
        throw new Error(startData.error || "Failed to start transcoding")
      }

      setUploadProgress(100)

      if (typeof window !== "undefined") {
        alert("Video uploaded. Processing has started.")
      }

      if (onComplete) onComplete()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Manual upload error:", error)
      if (typeof window !== "undefined") alert(error.message)
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  if (uploading) {
    return (
      <div className="py-4">
        <div className="mb-2 h-3 w-full rounded-full bg-gray-200">
          <div
            className="h-3 rounded-full bg-teal transition-all duration-300 ease-out"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
        <div className="flex justify-between text-sm text-gray">
          <span>{selectedFile?.name}</span>
          <span>{uploadProgress}%</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Metadata form */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray">Client</label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light/50">
            {clients.length === 0 ? (
              <option value="">No clients found</option>
            ) : (
              clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray">Recorded date</label>
          <input
            type="date"
            value={recordedAt}
            onChange={(e) => setRecordedAt(e.target.value)}
            className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light/50"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray">Title (optional)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short title"
            className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light/50"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray">Rating (1-5)</label>
          <select
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light/50">
            <option value="">-</option>
            {[1, 2, 3, 4, 5].map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Short description or transcript notes"
            className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light/50"
          />
        </div>
      </div>

      <div
        className={`relative cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors ${
          isDragOver
            ? "border-teal bg-teal-light/10"
            : selectedFile
            ? "border-green bg-green/5"
            : "border-gray-light hover:border-gray"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp4,.mov,.avi,.webm,video/*"
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div className="mb-3">
          {selectedFile ? (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <i className="icon-done text-xl text-green-600" />
            </div>
          ) : (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-ultralight">
              <i className="icon-film text-xl text-gray" />
            </div>
          )}
        </div>

        {selectedFile ? (
          <div>
            <h3 className="mb-1 text-base font-semibold text-green-900">File Selected</h3>
            <p className="font-medium text-green-700">{selectedFile.name}</p>
            <p className="text-sm text-green-600">{formatFileSize(selectedFile.size)}</p>
            <p className="mt-1 text-sm text-gray">Click to change file</p>
          </div>
        ) : (
          <div>
            <h3 className="mb-1 text-base font-semibold text-gray-dark">Upload a video review</h3>
            <p className="mb-2 text-gray">Drag and drop your video here, or click to browse</p>
            <p className="text-sm text-gray">Supports MP4, MOV, AVI, WebM up to 100MB</p>
          </div>
        )}
      </div>

      {selectedFile && (
        <div className="mt-4 text-center">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="mx-auto inline-flex items-center rounded-md bg-teal px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-dark disabled:cursor-not-allowed disabled:opacity-60">
            <i className="icon-upload mr-2" />
            Upload Video
          </button>
        </div>
      )}
    </div>
  )
}

export default VideoManualUploadForm
